// src/reco/semanticRanker.js

import { embedText } from "../api/openaiProxy";
import { cosineSimilarity } from "../utils/cosine";
import { buildProductSummary } from "../features/productSummary";
import {
  computeProductSourceHash,
  getProductEmbedding,
  setProductEmbedding,
} from "../state/productEmbeddingStore";
import { ensurePinFeaturesAndEmbedding } from "../features/pinFeaturePipeline";

// -----------------------------
// Helpers
// -----------------------------
function normalize(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildHaystack(product) {
  return normalize(
    [
      product.name,
      product.brand,
      product.category,
      product.variant,
      product.fit,
      product.budgetTier,
      ...(product.styleTags || []),
    ].join(" ")
  );
}

function matchesQuery(product, query) {
  const terms = normalize(query).split(" ").filter(Boolean);
  if (!terms.length) return true;

  const hay = buildHaystack(product);
  return terms.every((t) => hay.includes(t));
}

// -----------------------------
// Build pin embeddings
// -----------------------------
async function buildPinEmbeddings(editId, editItems) {
  const out = [];

  for (const pin of editItems || []) {
    const entry = await ensurePinFeaturesAndEmbedding(editId, pin);
    if (entry?.embedding?.length) {
      out.push(entry.embedding);
      continue;
    }

    const text =
      pin?.text ||
      pin?.title ||
      pin?.note ||
      pin?.caption ||
      null;

    if (text?.trim()) {
      try {
        const emb = await embedText(text);
        if (emb?.length) out.push(emb);
      } catch {}
    }
  }

  return out;
}

// -----------------------------
// MAIN RANKER
// -----------------------------
export async function rankProductsForQueryAsync(
  allProducts,
  editItems,
  editId,
  query,
  options = {}
) {
  const {
    useEditRanking = true,
  } = options;

  const hasQuery = !!query?.trim();

  // -----------------------------
  // STEP 1 — HARD FILTER (WORDS)
  // -----------------------------
  let filtered = allProducts;

  if (hasQuery) {
    filtered = allProducts.filter((p) => matchesQuery(p, query));
  }

  if (!filtered.length) return [];

  // -----------------------------
  // STEP 2 — EARLY EXIT
  // -----------------------------
  if (!useEditRanking && !hasQuery) {
    return filtered;
  }

  // -----------------------------
  // STEP 3 — EMBEDDINGS
  // -----------------------------
  const queryEmbedding = hasQuery ? await embedText(query) : null;

  const productEmbeddings = [];

  for (const p of filtered) {
    const hash = computeProductSourceHash(p);
    const cached = getProductEmbedding(p.id);

    let embedding;

    if (cached && cached.sourceHash === hash && cached.embedding?.length) {
      embedding = cached.embedding;
    } else {
      const summary = buildProductSummary(p);
      embedding = await embedText(summary);
      setProductEmbedding(p.id, { sourceHash: hash, embedding });
    }

    productEmbeddings.push({ product: p, embedding });
  }

  const pinEmbeddings = useEditRanking
    ? await buildPinEmbeddings(editId, editItems)
    : [];

  // -----------------------------
  // STEP 4 — SCORING
  // -----------------------------
  const QUERY_WEIGHT = 1.0;
  const PIN_WEIGHT = 0.4;

  const scored = productEmbeddings.map(({ product, embedding }) => {
    let score = 0;

    if (queryEmbedding) {
      score += cosineSimilarity(queryEmbedding, embedding) * QUERY_WEIGHT;

      // keyword boost
      if (buildHaystack(product).includes(normalize(query))) {
        score += 0.4;
      }
    }

    if (pinEmbeddings.length) {
      for (const pinEmb of pinEmbeddings) {
        score += cosineSimilarity(pinEmb, embedding) * PIN_WEIGHT;
      }
    }

    return { product, score };
  });

  scored.sort((a, b) => b.score - a.score);

  return scored.map((s) => s.product);
}